import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Switch, FormControlLabel, Alert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import axios from '../../utils/axios';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

const endpoint = '/sales-site/settings';

// TODO: Загрузка настроек с сервера
// const response = await axios.get('/api/sales-site/settings');
// setSettings(response.data);
// Временные данные для демонстрации
// TODO: Сохранение настроек на сервере
// await axios.put('/api/sales-site/settings', settings);

const readSettings = (response) => {
  const data = response?.data?.data;
  if (response?.data?.success !== true
    || typeof data?.siteName !== 'string'
    || typeof data?.contacts?.email !== 'string'
    || typeof data?.contacts?.telegram !== 'string'
    || typeof data?.auth?.requireAuth !== 'boolean'
    || typeof data?.auth?.allowRegistration !== 'boolean') {
    throw new Error('Некорректный ответ сервера');
  }
  return {
    siteName: data.siteName,
    contacts: { email: data.contacts.email, telegram: data.contacts.telegram },
    auth: { requireAuth: true, allowRegistration: data.auth.allowRegistration }
  };
};

const SalesSiteSettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canView = !permissionsLoading && currentUser?.role === 'curator'
    && hasPermission('salesSite', 'view');
  const canEdit = canView && hasPermission('salesSite', 'edit');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [reload, setReload] = useState(0);
  const sessionRef = useRef(0);
  const savingRef = useRef(false);

  useEffect(() => {
    const session = ++sessionRef.current;
    setSettings(null);
    setLoadError(false);
    setSaveError(false);
    setLoading(canView);
    setSaving(false);
    savingRef.current = false;
    if (!canView) return undefined;

    const loadSettings = async () => {
      try {
        const response = await axios.get(endpoint);
        if (session !== sessionRef.current) return;
        setSettings(readSettings(response));
      } catch (error) {
        if (session !== sessionRef.current) return;
        setLoadError(true);
        enqueueSnackbar('Ошибка загрузки настроек', { variant: 'error' });
      } finally {
        if (session === sessionRef.current) setLoading(false);
      }
    };
    loadSettings();
    return () => { sessionRef.current += 1; };
  }, [canView, currentUser?._id, reload, enqueueSnackbar]);

  const nameInvalid = settings !== null && !settings.siteName.trim();
  const emailInvalid = settings !== null && Boolean(settings.contacts.email.trim())
    && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settings.contacts.email.trim());
  const disabled = !canEdit || loading || saving;

  const handleChange = (field, group) => (event) => {
    if (disabled || !settings) return;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setSaveError(false);
    setSettings(prev => group
      ? { ...prev, [group]: { ...prev[group], [field]: value } }
      : { ...prev, [field]: value });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (disabled || savingRef.current || !settings || nameInvalid || emailInvalid || loadError) return;
    const session = sessionRef.current;
    savingRef.current = true;
    setSaving(true);
    setSaveError(false);
    try {
      const response = await axios.put(endpoint, {
        siteName: settings.siteName.trim(),
        contacts: {
          email: settings.contacts.email.trim(),
          telegram: settings.contacts.telegram.trim()
        },
        auth: {
          requireAuth: true,
          allowRegistration: settings.auth.allowRegistration
        }
      });
      if (session !== sessionRef.current) return;
      setSettings(readSettings(response));
      enqueueSnackbar('Настройки успешно сохранены', { variant: 'success' });
    } catch (error) {
      if (session !== sessionRef.current) return;
      setSaveError(true);
      enqueueSnackbar('Ошибка сохранения настроек', { variant: 'error' });
    } finally {
      if (session === sessionRef.current) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Настройки сайта продаж
        </Typography>
      </Box>
      {permissionsLoading ? (
        <Typography role="status">Проверка прав доступа...</Typography>
      ) : !canView ? (
        <Alert severity="error">У вас нет прав для просмотра этой страницы.</Alert>
      ) : loading ? (
        <Typography role="status">Загрузка настроек...</Typography>
      ) : loadError ? (
        <Alert severity="error" action={<Button color="inherit" onClick={() => setReload(value => value + 1)}>Повторить</Button>}>
          Ошибка загрузки настроек. Сохранение недоступно.
        </Alert>
      ) : settings && (
        <Box component="form" aria-label="Настройки сайта продаж" onSubmit={handleSave} noValidate sx={{ maxWidth: 800 }}>
          {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>Только просмотр</Alert>}
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>Ошибка сохранения настроек. Изменения не сохранены. Попробуйте ещё раз.</Alert>}
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Основные настройки</Typography>
              <TextField
                fullWidth required label="Название сайта" value={settings.siteName}
                onChange={handleChange('siteName')} disabled={disabled}
                error={nameInvalid} helperText={nameInvalid ? 'Введите название сайта' : ''}
              />
              <TextField
                fullWidth label="Email для связи" type="email" value={settings.contacts.email}
                onChange={handleChange('email', 'contacts')} disabled={disabled}
                error={emailInvalid} helperText={emailInvalid ? 'Введите корректный email' : ''}
              />
              <TextField
                fullWidth label="Telegram поддержка" value={settings.contacts.telegram}
                onChange={handleChange('telegram', 'contacts')} disabled={disabled}
                placeholder="@username или ссылка"
              />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Авторизация</Typography>
              <FormControlLabel
                control={<Switch checked={settings.auth.requireAuth} disabled />}
                label="Требовать авторизацию"
              />
              <Typography variant="body2" color="text.secondary">
                Вход на сайт обязателен для всех посетителей. Отключить авторизацию нельзя.
              </Typography>
              <FormControlLabel
                control={<Switch checked={settings.auth.allowRegistration} onChange={handleChange('allowRegistration', 'auth')} disabled={disabled} />}
                label="Разрешить регистрацию"
              />
            </CardContent>
          </Card>
          <Button
            type="submit" variant="contained" size="large" startIcon={<SaveIcon />}
            disabled={disabled || nameInvalid || emailInvalid} sx={{ mt: 2 }}
          >
            {saving ? 'Сохранение...' : 'Сохранить настройки'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SalesSiteSettings;
